import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService } from 'src/app/pages/core/abstraction/auth.service';
import { UPLOAD_TYPE } from 'src/app/pages/core/models/DocumentFlow';
import { DOCUMENT_ERRORS, DOCUMENT_STATUS, DOCUMENT_TYPE } from 'src/app/pages/core/models/User';

@Component({
  selector: 'app-upload-document',
  templateUrl: './upload-document.component.html',
  styleUrls: ['./upload-document.component.scss'],
})
export class UploadDocumentComponent implements OnInit {
  @Input() uploadType: UPLOAD_TYPE = UPLOAD_TYPE.INCOME;
  @Output() documentUploaded = new EventEmitter();
  @ViewChild('fileUploadInput') fileUploadInput;
  @ViewChild('cedularTaxUploadInput') cedularTaxUploadInput;

  UPLOAD_TYPE = UPLOAD_TYPE;
  DOCUMENT_TYPE = DOCUMENT_TYPE;
  documentType: DOCUMENT_TYPE;
  uploadedFiles: { name?: string; base64?: string; uploaded: boolean }[] = [];
  uploadError: string[] = [];
  loading = false;
  filesAreUploaded = false;
  showCertDocUpload = true;
  showCedularDocUpload = true;
  acceptedExtension = ['bmp', 'jpg', 'jpeg', 'pdf', 'png', 'tif', 'tiff'];

  constructor(private authService: AuthService) {}

  ngOnInit(): void {
    this.setDocumentType();
  }

  triggerUpload(isCedularTax = false) {
    if (this.loading) {
      return;
    }

    if (isCedularTax) {
      this.cedularTaxUploadInput.nativeElement.click();
    } else {
      this.fileUploadInput.nativeElement.click();
    }
  }

  readFile(event: Event) {
    this.uploadError = [];
    const target = event.target as HTMLInputElement;
    if (target.files.length > 0) {
      const filesToUpload = this.checkRepeateadFiles(target.files);
      if (filesToUpload.files.length === 0) {
        return;
      }

      filesToUpload.files.forEach((element) => {
        const reader = new FileReader();
        reader.readAsDataURL(element);

        reader.onload = (onloadEvent: ProgressEvent) => {
          const extension = target.files[0].name.split('.').pop().toLowerCase();
          // Check de tamaño 25 MB
          if (onloadEvent.total > 26300000 || this.acceptedExtension.indexOf(extension) === -1) {
            this.uploadError.push(
              'El tamaño máximo permitido es de 25 MB. Los formatos válidos son bmp, jpg, jpeg, pdf, png, tif, tiff.',
            );
            return;
          }
          const progressTarget = onloadEvent.target as FileReader;
          const file = {
            name: element.name,
            base64: progressTarget.result as string,
            uploaded: false,
          };

          this.uploadedFiles.push(file);
        };
        reader.onerror = (err) => {
          this.uploadError.push(`Ocurrió un error al subir "${element.name}".`);
          target.value = '';
        };
      });
    }
  }

  uploadFiles() {
    if (this.loading) {
      return;
    }
    this.loading = true;

    for (let i = 0; i < this.uploadedFiles.length; i++) {
      const file = this.uploadedFiles[i];
      if (file.uploaded) {
        continue;
      }

      this.authService
        .uploadImage(this.documentType, file.base64, undefined, undefined, true)
        .subscribe({
          next: () => {
            this.uploadedFiles[i].uploaded = true;
            this.filesAreUploaded = this.uploadedFiles.every((value) => value && value.uploaded);
            if (this.filesAreUploaded) {
              switch (this.documentType) {
                case DOCUMENT_TYPE.FUNDS:
                  this.authService.loggedUser.fundsDocuments.funds = DOCUMENT_STATUS.PENDING;
                  break;
                case DOCUMENT_TYPE.CERTIFICACION_CONTABLE:
                  this.authService.loggedUser.fundsDocuments.certificacionContable =
                    DOCUMENT_STATUS.PENDING;
                  break;
                case DOCUMENT_TYPE.IMPUESTO_CEDULAR:
                  this.authService.loggedUser.fundsDocuments.impuestoCedular =
                    DOCUMENT_STATUS.PENDING;
                  break;
              }

              this.loading = false;
              this.documentUploaded.emit();
            }
          },
          error: (err: HttpErrorResponse) => {
            this.uploadError.push(
              this.setErrorUploadDocument(
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                err.error.internalStatus as string,
                this.uploadedFiles[i].name,
              ),
            );
            this.loading = false;
          },
        });
    }
  }

  removeFile(name: string, event: Event) {
    if (this.loading) {
      return;
    }
    event.stopPropagation();
    this.uploadedFiles = this.uploadedFiles.filter((file) => {
      return file.name !== name;
    });

    if (this.uploadedFiles.length > 0) {
      this.filesAreUploaded = this.uploadedFiles.every((value) => value.uploaded);
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    this.fileUploadInput.nativeElement.value = '';
    this.uploadError = [];
  }

  setErrorUploadDocument(error: string, documentName: string) {
    switch (error) {
      case DOCUMENT_ERRORS.REPEATED_DOCUMENTATION:
        return `Ya subiste "${documentName}" anteriormente. Probá con una nueva.`;
      case DOCUMENT_ERRORS.USER_BLOCKED:
        return `No es posible subir el "${documentName}". Por seguridad, su usuario ha sido bloqueado, debe contactar a compliance@tiendacrypto.com.`;
      case DOCUMENT_ERRORS.TOO_MANY_PENDING:
        return 'Llegaste al límite de documentaciones subidas, por favor esperá la resolución de alguna de las que subiste. ';
      default:
        return 'Ocurrió un problema en la subida de la imagen, intente nuevamente.';
    }
  }

  setDocumentType() {
    switch (this.uploadType) {
      case UPLOAD_TYPE.INCOME:
        this.documentType = DOCUMENT_TYPE.FUNDS;
        break;
      case UPLOAD_TYPE.ARBITRAGE_CERTIFICATION:
        this.documentType = DOCUMENT_TYPE.CERTIFICACION_CONTABLE;
        break;
      case UPLOAD_TYPE.ARBITRAGE_CEDULAR:
        this.documentType = DOCUMENT_TYPE.IMPUESTO_CEDULAR;
        break;
    }
  }

  private checkRepeateadFiles(files: FileList): { repeated: string[]; files: File[] } {
    let result = {
      repeated: [],
      files: [],
    };

    for (let i = 0; i < files.length; i++) {
      const exist = this.uploadedFiles.some((file) => {
        if (file === null || file.uploaded) {
          return false;
        }
        return file.name === files[i].name;
      });

      if (exist) {
        result.repeated.push(files[i].name);
      } else {
        result.files.push(files[i]);
      }
    }

    return result;
  }
}
